import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { TagBadges } from '../../components/TagBadges';
import { formatPnlShort, shortAccount } from '../../utils/traderFormat';
import { FLOW_GROUPS } from '../flow/groups';
import type { TradeTypeId, WalletTrade } from './tradeTypes';
import type { MarketWallet } from './useMarketWallets';
import { WalletTrades } from './WalletTrades';

export const WALLET_GRID = '160px 40px 44px 200px 90px 60px minmax(0, 1fr)';

interface WalletRowProps {
    address: string;
    wallet: MarketWallet | null;
    maxExposure: number;
    color: string | null;
    selected: boolean;
    trades: readonly WalletTrade[] | undefined;
    tradesLoading: boolean;
    tradesFailed: boolean;
    onRetryTrades: () => void;
    tradeTypes: readonly TradeTypeId[];
    onTradeTypesChange: (types: TradeTypeId[]) => void;
    onToggle: () => void;
}

function usd(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `$${(abs / 1_000).toFixed(1)}k`;
    return `$${abs.toFixed(0)}`;
}

function sinceText(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return ` since ${date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })}`;
}

function Mono({
    children,
    color,
}: {
    children: React.ReactNode;
    color?: string;
}) {
    return (
        <Typography
            sx={{
                fontSize: '12px',
                fontFamily: 'monospace',
                textAlign: 'right',
                whiteSpace: 'nowrap',
                color: color ?? 'text.primary',
            }}
        >
            {children}
        </Typography>
    );
}

export function WalletRow({
    address,
    wallet,
    maxExposure,
    color,
    selected,
    trades,
    tradesLoading,
    tradesFailed,
    onRetryTrades,
    tradeTypes,
    onTradeTypesChange,
    onToggle,
}: WalletRowProps) {
    const theme = useTheme();
    const sideColor =
        wallet?.side === 'YES'
            ? theme.palette.success.main
            : wallet?.side === 'NO'
              ? theme.palette.error.main
              : theme.palette.text.disabled;
    const pnl = wallet?.total_pnl ?? null;
    const exposure = wallet ? Math.abs(wallet.net_exposure_usd) : 0;
    const share = maxExposure > 0 ? Math.min(1, exposure / maxExposure) : 0;
    const tags = wallet?.tags ?? [];

    return (
        <Box data-testid={`wallet-row-${address}`}>
            <Box
                role="button"
                aria-pressed={selected}
                onClick={onToggle}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: WALLET_GRID,
                    alignItems: 'center',
                    columnGap: 2,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selected
                        ? alpha(color ?? theme.palette.primary.main, 0.12)
                        : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: selected && color ? color : 'transparent',
                            border: selected && color ? 0 : 1,
                            borderColor: 'divider',
                        }}
                    />
                    <Typography
                        sx={{ fontSize: '12px', fontFamily: 'monospace' }}
                    >
                        {shortAccount(address)}
                    </Typography>
                    <IconButton
                        component={RouterLink}
                        to={`/markets/trader/${address}`}
                        size="small"
                        aria-label="Open the trader page"
                        onClick={(event: React.MouseEvent) =>
                            event.stopPropagation()
                        }
                        sx={{ p: 0.25 }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {FLOW_GROUPS.map(group => {
                        const membership = wallet?.group_memberships.find(
                            m => m.group === group.id
                        );
                        if (!membership) return null;
                        return (
                            <Tooltip
                                key={group.id}
                                title={`${group.label}${sinceText(membership.admitted_at)}`}
                            >
                                <Box
                                    data-testid={`group-${group.id}-${address}`}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: group.color,
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>

                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: sideColor,
                    }}
                >
                    {wallet
                        ? wallet.side === 'flat'
                            ? 'Flat'
                            : wallet.side
                        : '–'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            flex: 1,
                            height: 5,
                            borderRadius: '3px',
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                        }}
                    >
                        <Box
                            sx={{
                                width: `${share * 100}%`,
                                height: '100%',
                                bgcolor: sideColor,
                            }}
                        />
                    </Box>
                    <Box sx={{ width: 70, flexShrink: 0 }}>
                        <Mono>{wallet ? usd(exposure) : '–'}</Mono>
                    </Box>
                </Box>

                <Mono
                    color={
                        pnl === null || pnl === 0
                            ? theme.palette.text.secondary
                            : pnl > 0
                              ? theme.palette.success.main
                              : theme.palette.error.main
                    }
                >
                    {formatPnlShort(pnl)}
                </Mono>

                <Mono color={theme.palette.text.secondary}>
                    {wallet ? wallet.trade_count : '–'}
                </Mono>

                {/* A badge is a little taller than a line: it reaches into
                    the row's padding, so a row is as tall with tags as
                    without (and as the loading placeholder of a row). */}
                <Box sx={{ my: '-3px', minWidth: 0 }}>
                    <TagBadges tags={tags} />
                </Box>
            </Box>
            {selected && (
                <WalletTrades
                    trades={trades}
                    loading={tradesLoading}
                    failed={tradesFailed}
                    onRetry={onRetryTrades}
                    types={tradeTypes}
                    onTypesChange={onTradeTypesChange}
                />
            )}
        </Box>
    );
}
